import { useMemo, useState } from 'react';

import { ApiError } from '../../../api/errors';
import { useAuth } from '../../../auth/AuthProvider';
import { permissions } from '../../../auth/permissions';
import { useFacilities } from '../../organization/api/useFacilities';
import { ConfirmUserActionDialog } from '../components/ConfirmUserActionDialog';
import { EditRolesDialog } from '../components/EditRolesDialog';
import { InviteUserDialog } from '../components/InviteUserDialog';
import {
  useAssignRoles,
  useInviteUser,
  useReactivateUser,
  useRemoveUser,
  useResendInvitation,
  useSuspendUser,
  useUsers,
} from '../api/useUsers';
import type { MembershipStatus, RoleId, UserMembershipDto } from '../types/user';
import { roleLabel, statusLabel } from '../types/user';

type Filter = 'ALL' | MembershipStatus;
type PendingAction = { type: 'suspend' | 'reactivate' | 'remove'; membership: UserMembershipDto } | null;

const filters: Array<{ id: Filter; label: string }> = [
  { id: 'ALL', label: 'All' },
  { id: 'ACTIVE', label: 'Active' },
  { id: 'INVITED', label: 'Invited' },
  { id: 'SUSPENDED', label: 'Suspended' },
  { id: 'REMOVED', label: 'Removed' },
];

function errorText(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Route42 could not complete the request.';
}

function initials(email: string): string {
  const value = email.trim();
  if (!value) return 'U';
  const parts = value.split('@')[0].split(/[._-]+/).filter(Boolean);
  return (parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : value.slice(0, 2)).toUpperCase();
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
}

export function UsersPage() {
  const auth = useAuth();
  const usersQuery = useUsers();
  const facilitiesQuery = useFacilities();
  const inviteMutation = useInviteUser();
  const rolesMutation = useAssignRoles();
  const resendMutation = useResendInvitation();
  const suspendMutation = useSuspendUser();
  const reactivateMutation = useReactivateUser();
  const removeMutation = useRemoveUser();

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('ALL');
  const [showInvite, setShowInvite] = useState(false);
  const [editing, setEditing] = useState<UserMembershipDto | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const users = usersQuery.data?.users ?? [];
  const facilityNames = new Map((facilitiesQuery.data?.facilities ?? []).map((facility) => [facility.facility_id, facility.facility_name]));
  const activeAdminCount = users.filter((user) => user.status === 'ACTIVE' && user.role_ids.includes('customer_admin')).length;
  const entitlementFeatures = usersQuery.data?.customer.entitlements.features ?? [];
  const userAdministrationEnabled = entitlementFeatures.includes('user_administration');
  const maxUsers = usersQuery.data?.customer.entitlements.limits?.max_users ?? null;
  const consumingUsers = users.filter((user) => user.status !== 'REMOVED').length;

  const visibleUsers = useMemo(() => {
    const term = search.trim().toLocaleLowerCase();
    return users.filter((user) => {
      if (filter !== 'ALL' && user.status !== filter) return false;
      if (!term) return true;
      return user.email.toLocaleLowerCase().includes(term)
        || user.role_ids.some((role) => roleLabel(role).toLocaleLowerCase().includes(term));
    });
  }, [filter, search, users]);

  const canInvite = auth.can(permissions.usersInvite) && userAdministrationEnabled;
  const canAssignRoles = auth.can(permissions.usersAssignRole) && userAdministrationEnabled;

  async function runAction() {
    if (!pendingAction) return;
    setLocalError(null);
    try {
      if (pendingAction.type === 'suspend') await suspendMutation.mutateAsync(pendingAction.membership.membership_id);
      if (pendingAction.type === 'reactivate') await reactivateMutation.mutateAsync(pendingAction.membership.membership_id);
      if (pendingAction.type === 'remove') await removeMutation.mutateAsync(pendingAction.membership.membership_id);
      setNotice(`${pendingAction.membership.email} was ${pendingAction.type === 'suspend' ? 'suspended' : pendingAction.type === 'reactivate' ? 'reactivated' : 'removed'}.`);
      setPendingAction(null);
    } catch (error) {
      setLocalError(errorText(error));
    }
  }

  if (!auth.can(permissions.usersRead)) {
    return <section className="r42-page r42-page--wide"><div className="r42-empty-state r42-user-access-denied"><i className="bi bi-shield-lock" aria-hidden="true" /><h2>Users & access is not available</h2><p>Your current Route42 membership does not include permission to view organization users.</p></div></section>;
  }

  return (
    <section className="r42-page r42-page--wide r42-users-page">
      <div className="r42-page-header r42-users-header">
        <div>
          <span className="r42-eyebrow">Organization settings</span>
          <h1>Users & access</h1>
          <p>Manage who can access {usersQuery.data?.customer.customer_name ?? auth.activeMembership?.customer_name ?? 'this organization'} and what they can do.</p>
        </div>
        {canInvite ? <button className="btn r42-btn-primary" onClick={() => { setNotice(null); setShowInvite(true); }}><i className="bi bi-person-plus" aria-hidden="true" /> Invite user</button> : null}
      </div>

      {notice ? <div className="r42-user-notice" role="status"><i className="bi bi-check-circle" aria-hidden="true" /><span>{notice}</span><button onClick={() => setNotice(null)} aria-label="Dismiss message"><i className="bi bi-x-lg" aria-hidden="true" /></button></div> : null}

      <div className="r42-user-summary">
        <div><span className="r42-eyebrow">Members</span><strong>{consumingUsers}{maxUsers ? ` of ${maxUsers}` : ''}</strong><small>{maxUsers ? 'Current plan capacity' : 'Active and invited memberships'}</small></div>
        <div><span className="r42-eyebrow">Active</span><strong>{users.filter((user) => user.status === 'ACTIVE').length}</strong><small>Can currently access Route42</small></div>
        <div><span className="r42-eyebrow">Pending</span><strong>{users.filter((user) => user.status === 'INVITED').length}</strong><small>Invitations awaiting activation</small></div>
        <div><span className="r42-eyebrow">Plan</span><strong>{usersQuery.data?.customer.entitlements.display_name ?? 'MVP'}</strong><small>User administration {userAdministrationEnabled ? 'enabled' : 'not included'}</small></div>
      </div>

      <div className="r42-user-toolbar">
        <label className="r42-search">
          <i className="bi bi-search" aria-hidden="true" />
          <input aria-label="Search users" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search email or role" />
        </label>
        <div className="r42-user-filters" aria-label="User status filters">
          {filters.map((item) => <button key={item.id} className={filter === item.id ? 'active' : ''} onClick={() => setFilter(item.id)}>{item.label}</button>)}
        </div>
      </div>

      {usersQuery.isPending ? (
        <div className="r42-list-panel" aria-label="Loading users">{[1, 2, 3].map((item) => <div className="r42-skeleton-row" key={item}><span /><div><b /><small /></div></div>)}</div>
      ) : usersQuery.isError ? (
        <div className="r42-inline-state r42-inline-state--error"><span>{errorText(usersQuery.error)}</span><button className="btn btn-outline-secondary btn-sm" onClick={() => void usersQuery.refetch()}>Try again</button></div>
      ) : visibleUsers.length === 0 ? (
        <div className="r42-empty-state r42-user-empty"><i className="bi bi-people" aria-hidden="true" /><h2>No matching users</h2><p>Try another search or status filter.</p></div>
      ) : (
        <div className="r42-user-list" aria-label="Organization users">
          <div className="r42-user-list-head" aria-hidden="true"><span>User</span><span>Access</span><span>Facility scope</span><span>Status</span><span /></div>
          {visibleUsers.map((membership) => {
            const isSelf = Boolean(membership.user_id && membership.user_id === auth.profile?.user.user_id);
            const protectedAdmin = membership.status === 'ACTIVE' && membership.role_ids.includes('customer_admin') && activeAdminCount <= 1;
            const canSuspend = auth.can(permissions.usersSuspend) && membership.status === 'ACTIVE' && !protectedAdmin;
            const canReactivate = auth.can(permissions.usersReactivate) && membership.status === 'SUSPENDED';
            const canRemove = auth.can(permissions.usersRemove) && membership.status !== 'REMOVED' && !protectedAdmin;
            const hasActions = (canAssignRoles && membership.status !== 'REMOVED') || canSuspend || canReactivate || canRemove || (auth.can(permissions.usersInvite) && membership.status === 'INVITED');
            return (
              <div className="r42-user-row" key={membership.membership_id}>
                <div className="r42-user-identity">
                  <span className="r42-user-avatar">{initials(membership.email)}</span>
                  <div><strong>{membership.email}</strong><small>{isSelf ? 'You · ' : ''}{membership.user_id ? `Joined ${formatDate(membership.activated_at)}` : `Invited ${formatDate(membership.invited_at)}`}</small></div>
                </div>
                <div className="r42-user-roles">{membership.role_ids.map((role) => <span key={role}>{roleLabel(role)}</span>)}</div>
                <div className="r42-user-scope"><strong>{membership.facility_ids.length ? `${membership.facility_ids.length} facilit${membership.facility_ids.length === 1 ? 'y' : 'ies'}` : 'All facilities'}</strong>{membership.facility_ids.length ? <small title={membership.facility_ids.map((id) => facilityNames.get(id) ?? id).join(', ')}>{membership.facility_ids.map((id) => facilityNames.get(id) ?? id).join(', ')}</small> : <small>Organization-wide</small>}</div>
                <div><span className={`r42-user-status r42-user-status--${membership.status.toLocaleLowerCase()}`}>{statusLabel(membership.status)}</span>{protectedAdmin ? <small className="r42-user-protected">Required admin</small> : null}</div>
                <div className="r42-user-actions dropdown">
                  {hasActions ? <>
                    <button className="r42-icon-button" data-bs-toggle="dropdown" aria-label={`Actions for ${membership.email}`}><i className="bi bi-three-dots" aria-hidden="true" /></button>
                    <ul className="dropdown-menu dropdown-menu-end">
                      {canAssignRoles && membership.status !== 'REMOVED' ? <li><button className="dropdown-item" onClick={() => { setLocalError(null); setEditing(membership); }}>Edit roles</button></li> : null}
                      {auth.can(permissions.usersInvite) && membership.status === 'INVITED' ? <li><button className="dropdown-item" onClick={async () => {
                        setLocalError(null);
                        try {
                          const result = await resendMutation.mutateAsync(membership.membership_id);
                          if (result.notification?.status === 'ORGANIZATION_INVITATION_SENT') {
                            setNotice(`Organization invitation resent to ${membership.email}. Their existing Route42 credentials were not changed.`);
                          } else if (result.identity.invitation_requested) {
                            setNotice(`A fresh Route42 temporary-password invitation was sent to ${membership.email}.`);
                          } else {
                            setNotice(`The invitation state for ${membership.email} was refreshed.`);
                          }
                        } catch (error) {
                          setNotice(null);
                          setLocalError(errorText(error));
                        }
                      }}>Resend invitation</button></li> : null}
                      {canSuspend ? <li><button className="dropdown-item" onClick={() => { setLocalError(null); setPendingAction({ type: 'suspend', membership }); }}>Suspend access</button></li> : null}
                      {canReactivate ? <li><button className="dropdown-item" onClick={() => { setLocalError(null); setPendingAction({ type: 'reactivate', membership }); }}>Reactivate access</button></li> : null}
                      {canRemove ? <><li><hr className="dropdown-divider" /></li><li><button className="dropdown-item text-danger" onClick={() => { setLocalError(null); setPendingAction({ type: 'remove', membership }); }}>Remove from organization</button></li></> : null}
                    </ul>
                  </> : <span className="r42-user-no-actions">—</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {localError && !editing && !pendingAction ? <div className="r42-form-alert r42-user-page-error" role="alert"><i className="bi bi-exclamation-circle" aria-hidden="true" /><div><strong>Route42 could not complete that action.</strong><span>{localError}</span></div></div> : null}

      {showInvite ? <InviteUserDialog
        facilities={facilitiesQuery.data?.facilities ?? []}
        allowOrganizationWide={!auth.activeMembership?.facility_ids.length}
        busy={inviteMutation.isPending}
        error={inviteMutation.isError ? errorText(inviteMutation.error) : null}
        onClose={() => { if (!inviteMutation.isPending) setShowInvite(false); }}
        onSubmit={async (input) => {
          try {
            const result = await inviteMutation.mutateAsync(input);
            setShowInvite(false);
            const delivery = result.notification?.status === 'ORGANIZATION_INVITATION_SENT'
              ? ' Their existing Route42 identity was notified by email.'
              : result.identity.invitation_requested
                ? ' A Route42 temporary-password invitation was sent by email.'
                : '';
            setNotice(`${result.membership.email} was invited as ${roleLabel(input.roleId)}.${delivery}`);
          } catch { /* mutation state renders the error */ }
        }}
      /> : null}

      {editing ? <EditRolesDialog
        membership={editing}
        protectCustomerAdmin={editing.status === 'ACTIVE' && editing.role_ids.includes('customer_admin') && activeAdminCount <= 1}
        busy={rolesMutation.isPending}
        error={rolesMutation.isError ? errorText(rolesMutation.error) : null}
        onClose={() => { if (!rolesMutation.isPending) setEditing(null); }}
        onSubmit={async (roleIds: RoleId[]) => {
          try {
            await rolesMutation.mutateAsync({ membershipId: editing.membership_id, roleIds });
            setEditing(null);
            setNotice(`${editing.email}'s roles were updated.`);
          } catch { /* mutation state renders the error */ }
        }}
      /> : null}

      {pendingAction ? <ConfirmUserActionDialog
        title={pendingAction.type === 'suspend' ? 'Suspend this user?' : pendingAction.type === 'reactivate' ? 'Reactivate this user?' : 'Remove this user?'}
        description={pendingAction.type === 'suspend'
          ? `${pendingAction.membership.email} will immediately lose access to this organization but can be reactivated later.`
          : pendingAction.type === 'reactivate'
            ? `${pendingAction.membership.email} will regain access using their existing Route42 identity.`
            : `${pendingAction.membership.email} will be removed from this organization. Their Route42 identity and memberships in other organizations are not deleted.`}
        actionLabel={pendingAction.type === 'suspend' ? 'Suspend access' : pendingAction.type === 'reactivate' ? 'Reactivate' : 'Remove user'}
        tone={pendingAction.type === 'remove' ? 'danger' : 'primary'}
        busy={suspendMutation.isPending || reactivateMutation.isPending || removeMutation.isPending}
        error={localError}
        onClose={() => { if (!suspendMutation.isPending && !reactivateMutation.isPending && !removeMutation.isPending) { setPendingAction(null); setLocalError(null); } }}
        onConfirm={() => void runAction()}
      /> : null}
    </section>
  );
}
